import { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, Check } from 'lucide-react';

// Region abbreviation mapping for display
const REGION_ABBREVIATIONS: Record<string, string> = {
  'All': 'All',
  'Northern': 'N',
  'Northeastern': 'NE', 
  'Central': 'C',
  'Western': 'W',
  'Eastern': 'E',
  'Southern': 'S'
};

interface FilterSitesProvinceProps {
  selectedRegion: string;
  setSelectedRegion: (region: string) => void;
  selectedSites: string[];
  setSelectedSites: (sites: string[]) => void;
  sitesGeoData: any[];
  availableRegions?: string[]; // Dynamic regions from API
}

export function FilterSitesProvince({
  selectedRegion,
  setSelectedRegion,
  selectedSites,
  setSelectedSites,
  sitesGeoData,
  availableRegions,
}: FilterSitesProvinceProps) {
  const [showSitesDropdown, setShowSitesDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Create regions list from availableRegions prop or fall back to default
  const regions = useMemo(() => {
    if (availableRegions && availableRegions.length > 0) {
      return availableRegions.map(region => ({
        key: region,
        label: REGION_ABBREVIATIONS[region] || region.charAt(0).toUpperCase()
      }));
    }
    // Fallback to default regions if no API data available
    return [
      { key: 'All', label: 'All' },
      { key: 'Northern', label: 'N' },
      { key: 'Northeastern', label: 'NE' },
      { key: 'Central', label: 'C' },
      { key: 'Western', label: 'W' },
      { key: 'Eastern', label: 'E' },
      { key: 'Southern', label: 'S' }
    ];
  }, [availableRegions]);

  // Filter sites based on selected region
  const filteredSites = selectedRegion === 'All' 
    ? sitesGeoData 
    : sitesGeoData.filter(site => site.region === selectedRegion);

  const availableSites = filteredSites;

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSitesDropdown(false);
      }
    }

    if (showSitesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSitesDropdown]);

  const handleRegionChange = (region: string) => {
    setSelectedRegion(region);
    // Auto-select all sites in the new region
    if (region === 'All') {
      setSelectedSites(sitesGeoData.map(s => s.code));
    } else {
      const regionSites = sitesGeoData.filter(site => site.region === region);
      setSelectedSites(regionSites.map(s => s.code));
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Region Toggle Buttons */}
      <div className="flex items-center gap-1">
        <span className="text-xs font-medium text-dark-grey mr-2">Region:</span>
        <div className="flex bg-white rounded-md border border-light-grey shadow-sm overflow-hidden">
          {regions.map(region => (
            <button
              key={region.key}
              onClick={() => handleRegionChange(region.key)}
              className={`px-3 py-1.5 text-xs font-medium transition-all border-r border-light-grey last:border-r-0 ${
                selectedRegion === region.key
                  ? 'bg-dark-grey text-white'
                  : 'text-dark-grey hover:bg-bg hover:text-black'
              }`}
            >
              {region.label}
            </button>
          ))}
        </div>
      </div>

      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowSitesDropdown(!showSitesDropdown)}
          className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white rounded-md border border-light-grey hover:border-dark-grey transition-all shadow-sm"
        >
          <span className="text-xs text-dark-grey">Property:</span>
          <span className="text-xs font-medium text-black">{selectedSites.length} Properties</span>
          <ChevronDown className="w-3.5 h-3.5 text-dark-grey ml-1" />
        </button>
        
        {showSitesDropdown && (
          <div className="absolute z-10 mt-1 w-64 bg-white rounded-md shadow-card border border-light-grey max-h-64 overflow-y-auto">
            <button
              onClick={() => {
                if (selectedSites.length === availableSites.length) {
                  setSelectedSites([]);
                } else {
                  setSelectedSites(availableSites.map(s => s.code));
                }
              }}
              className="block w-full text-left px-3 py-2 paragraph-10 hover:bg-bg border-b border-light-grey font-medium text-dark-grey transition-colors"
            >
              {selectedSites.length === availableSites.length ? 'Deselect All' : 'Select All'}
            </button>
            {availableSites.map(site => (
              <button
                key={site.id}
                onClick={() => {
                  if (selectedSites.includes(site.code)) {
                    setSelectedSites(selectedSites.filter(s => s !== site.code));
                  } else {
                    setSelectedSites([...selectedSites, site.code]);
                  }
                }}
                className="flex items-center justify-between w-full text-left px-3 py-2 paragraph-10 hover:bg-bg text-dark-grey hover:text-black transition-colors"
              >
                <span>{site.name}</span>
                {selectedSites.includes(site.code) && (
                  <Check className="w-3.5 h-3.5 text-green" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}